<template>
  <main id="app">
    <img
      src="/android-chrome-192x192.png"
      alt="Muted Pokeball colors with an arrow pointing upwards along the bottom right cuve of the ball to represent upping your pokemon's stats."
      class="mx-auto"
    />
    <h1 class="text-4xl font-bold mb-4">EV Training Checker</h1>
    <p class="mb-2">
      Start typing a pokemon's name to filter the list!<br />You can use the
      pipe (|) character to chain searches for whicever location you're EV
      Training at!
    </p>
    <p class="mb-4">
      What is EV Training?
      <a
        href="https://www.google.com/url?sa=t&rct=j&q=&esrc=s&source=web&cd=&cad=rja&uact=8&ved=2ahUKEwjMy636rfn7AhX1E1kFHXQoA5YQFnoECAoQAQ&url=https%3A%2F%2Fmarriland.com%2Fguides%2Fev-training%2F&usg=AOvVaw3t6ad-yX8ESNG21hprKeV4"
        class="underline text-green-600"
        >Read about it</a
      >
      |
      <a
        href="https://www.youtube.com/watch?v=UEXySi4NGdw"
        class="underline text-green-600"
        >Watch about it</a
      >
    </p>
    <VueFuse
      :fuse-opts="opts"
      :keys="['name']"
      :list="pokemon"
      @fuse-results="handleResults"
      class="my-4 mx-auto p-4 text-xl border-2 rounded mr-4 bg-transparent"
    />
    <button
      v-if="!showTracking"
      class="rounded border-2 bg-purple-200 p-4 text-xl border-purple-700 hover:bg-purple-100 dark:bg-purple-700 dark:hover:bg-purple-800"
      @click="enableTracking"
    >
      Enable Tracking
    </button>
    <button
      v-else
      class="rounded border-2 bg-purple-200 p-4 text-xl border-purple-700 hover:bg-purple-100 dark:bg-purple-700 dark:hover:bg-purple-800"
      @click="resetCounter"
    >
      Reset Counter
    </button>
    <table class="mx-auto font-bold text-sm md:text-lg dark:text-white">
      <tr class="sticky top-0 bg-white dark:bg-gray-800">
        <th></th>
        <th>Pokemon</th>
        <th>Hp</th>
        <th>Atk</th>
        <th>Def</th>
        <th>Sp Atk</th>
        <th>Sp Def</th>
        <th>Spe</th>
        <th></th>
      </tr>
      <tr v-if="showTracking">
        <td></td>
        <td>Held Item:</td>
        <td>
          <button
            title="Power Weight"
            @click="setModifier('hp', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.hp === 8 }"
          >
            <img
              src="/items/power-weight.png"
              alt="Image of Power Weight Item"
            />
          </button>
        </td>
        <td>
          <button
            title="Power Bracer"
            @click="setModifier('atk', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.atk === 8 }"
          >
            <img
              src="/items/power-bracer.png"
              alt="Image of Power Bracer Item"
            />
          </button>
        </td>
        <td>
          <button
            title="Power Belt"
            @click="setModifier('def', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.def === 8 }"
          >
            <img src="/items/power-belt.png" alt="Image of Power Belt Item" />
          </button>
        </td>
        <td>
          <button
            title="Power Lens"
            @click="setModifier('spa', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.spa === 8 }"
          >
            <img src="/items/power-lens.png" alt="Image of Power Lens Item" />
          </button>
        </td>
        <td>
          <button
            title="Power Band"
            @click="setModifier('spd', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.spd === 8 }"
          >
            <img src="/items/power-band.png" alt="Image of Power Band Item" />
          </button>
        </td>
        <td>
          <button
            title="Power Anklet"
            @click="setModifier('spe', 8)"
            class="hover:bg-gray-200 rounded p-2 mx-2"
            :class="{ 'bg-gray-200': evModifier.spe === 8 }"
          >
            <img
              src="/items/power-anklet.png"
              alt="Image of Power Anklet Item"
            />
          </button>
        </td>
        <td></td>
      </tr>
      <tr
        v-if="showTracking"
        class="sticky top-14 md:top-6 bg-white dark:bg-gray-800"
      >
        <td></td>
        <td></td>
        <td>{{ evsEarned.hp }}</td>
        <td>{{ evsEarned.atk }}</td>
        <td>{{ evsEarned.def }}</td>
        <td>{{ evsEarned.spa }}</td>
        <td>{{ evsEarned.spd }}</td>
        <td>{{ evsEarned.spe }}</td>
        <td></td>
      </tr>
      <tr v-for="(poke, index) in pokeResults" :key="index" class="my-2">
        <td class="p-2">
          <img
            v-if="poke.refIndex + 1 < 906"
            :src="'/sprites/' + (poke.refIndex + 1) + '.png'"
            :alt="'Sprite for ' + poke.item.name"
            width="96px"
          />
          <img
            v-else
            src="/sprites/0.png"
            alt="Missing Sprite Question Mark Image"
            width="96px"
          />
        </td>
        <td class="p-2">{{ poke.item.name }}</td>
        <td class="p-2 h-8 w-8 md:w-20 bg-green-200 dark:bg-green-800">
          {{ poke.item.hp }}
        </td>
        <td class="p-2 w-8 md:w-20 bg-red-200 dark:bg-red-800">
          {{ poke.item.atk }}
        </td>
        <td class="p-2 w-8 md:w-20 bg-orange-200 dark:bg-orange-800">
          {{ poke.item.def }}
        </td>
        <td class="p-2 w-8 md:w-20 bg-pink-200 dark:bg-pink-800">
          {{ poke.item.spa }}
        </td>
        <td class="p-2 w-8 md:w-20 bg-yellow-200 dark:bg-yellow-800">
          {{ poke.item.spd }}
        </td>
        <td class="p-2 w-8 md:w-20 bg-blue-200 dark:bg-blue-800">
          {{ poke.item.spe }}
        </td>
        <td class="p-2 w-8 md:w-16">
          <button
            v-if="showTracking"
            @click="
              knockout({
                hp: poke.item.hp,
                atk: poke.item.atk,
                def: poke.item.def,
                spa: poke.item.spa,
                spd: poke.item.spd,
                spe: poke.item.spe,
              })
            "
            class="bg-gray-200 border-2 px-4 py-2 rounded hover:bg-white dark:bg-purple-700 dark:hover:bg-purple-800"
          >
            KO
          </button>
        </td>
      </tr>
    </table>
  </main>
</template>

<script>
import axios from "axios";
import VueFuse from "vue-fuse";

export default {
  name: "App",
  components: {
    VueFuse,
  },
  data() {
    return {
      pokemon: [],
      pokeResults: [],
      opts: {
        keys: ["name"],
        useExtendedSearch: true,
      },
      showTracking: false,
      evsEarned: {
        hp: 0,
        atk: 0,
        def: 0,
        spa: 0,
        spd: 0,
        spe: 0,
      },
      evModifier: {
        hp: 0,
        atk: 0,
        def: 0,
        spa: 0,
        spd: 0,
        spe: 0,
      },
    };
  },
  mounted() {
    axios.get("/pkData.json").then((res) => {
      this.pokemon = Object.values(res.data);
    });
  },
  methods: {
    handleResults(r) {
      this.pokeResults = r;
    },
    enableTracking() {
      this.showTracking = true;
    },
    resetCounter() {
      this.evsEarned = {
        hp: 0,
        atk: 0,
        def: 0,
        spa: 0,
        spd: 0,
        spe: 0,
      };
    },
    setModifier(stat, value) {
      this.evModifier = {
        hp: 0,
        atk: 0,
        def: 0,
        spa: 0,
        spd: 0,
        spe: 0,
      };
      this.evModifier[stat] = value;
    },
    knockout(evs) {
      if (
        this.evsEarned.hp +
          this.evsEarned.atk +
          this.evsEarned.def +
          this.evsEarned.spa +
          this.evsEarned.spd +
          this.evsEarned.spe <=
        508
      ) {
        // earn EVs
        this.evsEarned = {
          hp: Math.min(
            252,
            parseInt(this.evsEarned.hp) +
              parseInt(evs.hp) +
              parseInt(this.evModifier.hp),
          ),
          atk: Math.min(
            252,
            parseInt(this.evsEarned.atk) +
              parseInt(evs.atk) +
              parseInt(this.evModifier.atk),
          ),
          def: Math.min(
            252,
            parseInt(this.evsEarned.def) +
              parseInt(evs.def) +
              parseInt(this.evModifier.def),
          ),
          spa: Math.min(
            252,
            parseInt(this.evsEarned.spa) +
              parseInt(evs.spa) +
              parseInt(this.evModifier.spa),
          ),
          spd: Math.min(
            252,
            parseInt(this.evsEarned.spd) +
              parseInt(evs.spd) +
              parseInt(this.evModifier.spd),
          ),
          spe: Math.min(
            252,
            parseInt(this.evsEarned.spe) +
              parseInt(evs.spe) +
              parseInt(this.evModifier.spe),
          ),
        };
      } else {
        alert("Pokemon already has Max EVs earned");
      }
    },
  },
};
</script>
